import { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../contexts/TenantContext';

/**
 * useCashbackList — fetches cashback_records + decrypts amounts via the
 * `generic-decrypt-cashback` Edge Function, grouped by period (text col).
 *
 * R1 : tenant_id + agency_id filter.
 * R10 : 1000-row paginated.
 * R2 : decryption via authenticated Edge Function. Falls back to nulls on error.
 *
 * Params :
 *  - status : optional filter (pending/approved/paid/rejected)
 *
 * Returns :
 *  - rows : array<{
 *      key, period, status, count, totalAmount,
 *      records: array<{ id, creator_id, creator_name, amount, status }>,
 *    }>
 *  - decryptionAvailable / loading / error / reload
 */
export function useCashbackList({ status = null } = {}) {
  const { tenantId, agencyId, loading: tenantLoading } = useTenant();
  const [raw, setRaw] = useState([]);
  const [creatorMap, setCreatorMap] = useState(new Map());
  const [decrypted, setDecrypted] = useState({});
  const [decryptionAvailable, setDecryptionAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      // 1) cashback_records paginated
      const PAGE = 1000;
      const all = [];
      let from = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let q = supabase
          .from('black_cashback_records')
          .select('id, tenant_id, agency_id, creator_id, period, status, amount_enc, created_at')
          .eq('tenant_id', tenantId)
          .order('period', { ascending: false });
        if (agencyId) q = q.eq('agency_id', agencyId);
        if (status)   q = q.eq('status', status);
        const { data, error: e1 } = await q.range(from, from + PAGE - 1);
        if (e1) throw e1;
        const rows = data || [];
        all.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }

      // 2) creator names
      const creatorIds = Array.from(new Set(all.map((r) => r.creator_id).filter(Boolean)));
      const map = new Map();
      if (creatorIds.length > 0) {
        const { data: c } = await supabase
          .from('black_creators')
          .select('id, display_name, tiktok_username')
          .in('id', creatorIds);
        for (const cr of c || []) map.set(cr.id, cr);
      }

      // 3) Decrypt via Edge Function (batched max 500 ids per call)
      const allIds = all.map((r) => r.id);
      const decryptedMap = {};
      let decryptOk = true;
      if (allIds.length > 0) {
        const BATCH = 500;
        for (let i = 0; i < allIds.length; i += BATCH) {
          const slice = allIds.slice(i, i + BATCH);
          try {
            const { data: out, error: fnErr } = await supabase.functions.invoke('black-decrypt-cashback', {
              body: { cashbackIds: slice },
            });
            if (fnErr) throw fnErr;
            if (out && typeof out === 'object') Object.assign(decryptedMap, out);
          } catch (e) {
            console.warn('[useCashbackList] decrypt batch failed (keeping AES badges):', e?.message || e);
            decryptOk = false;
            break;
          }
        }
      }

      setRaw(all);
      setCreatorMap(map);
      setDecrypted(decryptedMap);
      setDecryptionAvailable(decryptOk);
    } catch (e) {
      console.error('[useCashbackList] error:', e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [tenantId, agencyId, status]);

  useEffect(() => {
    if (!tenantLoading) fetchAll();
  }, [fetchAll, tenantLoading]);

  // Group by period
  const rows = useMemo(() => {
    const groups = new Map();
    for (const r of raw) {
      const key = r.period || 'no-period';
      const cur = groups.get(key) || {
        key,
        period: r.period,
        status: r.status,
        count: 0,
        totalAmount: 0,
        hasAmount: false,
        records: [],
      };
      const cr = r.creator_id ? creatorMap.get(r.creator_id) : null;
      const amt = decrypted[r.id]?.amount ?? null;
      cur.count += 1;
      cur.records.push({
        id: r.id,
        creator_id: r.creator_id,
        creator_name: cr?.display_name || cr?.tiktok_username || null,
        amount: amt,
        status: r.status,
      });
      if (amt != null) { cur.totalAmount += amt; cur.hasAmount = true; }
      // Group status = lowest priority (pending > approved > paid > rejected)
      const priority = { pending: 0, approved: 1, paid: 2, rejected: 3 };
      if ((priority[r.status] ?? 0) < (priority[cur.status] ?? 0)) cur.status = r.status;
      groups.set(key, cur);
    }
    return Array.from(groups.values()).map((g) => ({
      ...g,
      totalAmount: g.hasAmount ? g.totalAmount : null,
      decryptionAvailable,
    })).sort((a, b) => (b.period || '').localeCompare(a.period || ''));
  }, [raw, creatorMap, decrypted, decryptionAvailable]);

  return { rows, decryptionAvailable, loading: loading || tenantLoading, error, reload: fetchAll };
}
